/**
 * Typewriter speech bubble. Reveals `text` character by character at
 * `charDelayMs`. When text changes mid-animation, fast-forwards to the
 * new value's length and restarts the reveal. Respects the 140-char
 * ceiling per spec §4.6 L270 by trimming on render; callers pushing
 * long streams should split into multiple bubbles.
 */
import { useEffect, useState } from 'react';

export type SpeechBubbleProps = {
  text: string;
  charDelayMs?: number;
  /** Bubble variant — assistant (left-tail) vs character (right-tail). */
  tail?: 'left' | 'right' | 'none';
  className?: string;
  testId?: string;
};

const MAX_CHARS = 140;

export function SpeechBubble({
  text,
  charDelayMs = 30,
  tail = 'none',
  className = '',
  testId,
}: SpeechBubbleProps) {
  const truncated = text.length > MAX_CHARS ? `${text.slice(0, MAX_CHARS - 1)}…` : text;
  const [shown, setShown] = useState('');

  useEffect(() => {
    if (!truncated) {
      setShown('');
      return;
    }
    setShown(truncated.slice(0, 1));
    let i = 1;
    const id = window.setInterval(() => {
      if (i >= truncated.length) {
        window.clearInterval(id);
        return;
      }
      i += 1;
      setShown(truncated.slice(0, i));
    }, charDelayMs);
    return () => window.clearInterval(id);
  }, [truncated, charDelayMs]);

  if (!truncated) return null;
  const tailClass =
    tail === 'left'
      ? 'after:content-[""] after:absolute after:left-2 after:-bottom-2 after:border-8 after:border-transparent after:border-t-bg-elev'
      : tail === 'right'
        ? 'after:content-[""] after:absolute after:right-2 after:-bottom-2 after:border-8 after:border-transparent after:border-t-bg-elev'
        : '';
  return (
    <div
      className={`relative w-max max-w-[22ch] whitespace-pre-line px-2 py-1 rounded-md bg-bg-elev text-fg text-xs shadow border border-border ${tailClass} ${className}`}
      data-testid={testId}
    >
      <span aria-live="polite">{shown}</span>
    </div>
  );
}
